import React from 'react';
import { Box, Stack, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { Button, Loader, TextField } from '@orijinworks/frontend-commons';
import { useParams } from 'react-router-dom';
import FormFormioBuilder from './FormFormioBuilder';
import { useFormManageHook } from './useFormManageHook';
import { NavyMainTypography } from '../modules/Feeds/Feeds.style';

const FormManageDescription = ({ isEditMode }) => (
  <Typography variant="body2" color="text.secondary">
    {isEditMode
      ? 'Update the Form.io form name, active state, and fields below. The saved schema is Form.io form JSON.'
      : 'Form.io form name is required. Build the form below; the saved schema is Form.io form JSON.'}
  </Typography>
);

const FormManageFooterActions = ({ isEditMode, isBusy, handleBackToFeeds }) => (
  <Stack
    direction="row"
    justifyContent="flex-end"
    alignItems="center"
    spacing={2}
    sx={{ flexShrink: 0, pt: 1 }}
  >
    {!isEditMode ? (
      <Button type="button" variant="outlined" color="primary" onClick={handleBackToFeeds} disabled={isBusy}>
        Cancel
      </Button>
    ) : null}
    <Button type="submit" variant="contained" color="primary" disabled={isBusy}>
      {isEditMode ? 'Update form' : 'Create Form'}
    </Button>
  </Stack>
);

/**
 * Form management screen — create (`/forms`) or edit (`/forms/:formId`).
 * API: portal `POST/PUT /api/content/form` with `name`, `schema`, `is_active`.
 * `schema` stores a Form.io form JSON (`display`, `components`, …).
 */
const FormManage = React.memo(() => {
  const { formId } = useParams();
  const {
    formName,
    isActive,
    builderInitialForm,
    schemaEditorKey,
    nameError,
    pageLoading,
    isBusy,
    isEditMode,
    handleFormNameChange,
    handleIsActiveChange,
    handleFormSchemaChange,
    handleSubmit,
    handleBackToFeeds,
  } = useFormManageHook();

  return (
    <>
      <Loader isLoading={isBusy} />
      <Stack
        component="form"
        onSubmit={handleSubmit}
        height="100%"
        minHeight="100%"
        backgroundColor="white"
        padding="24px"
        spacing={3}
        maxWidth={1400}
        width="100%"
        margin="0 auto"
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <NavyMainTypography variant="h1">
            {isEditMode ? 'Edit form' : 'Create form'}
          </NavyMainTypography>
          <Button type="button" variant="text" onClick={handleBackToFeeds} disabled={isBusy}>
            Back to Feeds
          </Button>
        </Stack>
        <FormManageDescription isEditMode={isEditMode} />
        <TextField
          fullWidth
          required
          label="Form.io form name"
          name="formName"
          value={formName}
          onChange={handleFormNameChange}
          error={Boolean(nameError)}
          helperText={nameError || ' '}
          disabled={pageLoading}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isActive}
              onChange={handleIsActiveChange}
              color="primary"
              disabled={pageLoading}
            />
          }
          label="Is active"
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2 }}>
          <Typography variant="subtitle2" color="text.primary" sx={{ pt: 0 }}>
            Form builder
          </Typography>
          <Box sx={{ flex: 1, minHeight: 280, overflow: 'auto' }}>
            <FormFormioBuilder
              key={`${formId ?? 'new'}-${schemaEditorKey}`}
              initialForm={builderInitialForm}
              onChange={handleFormSchemaChange}
              disabled={pageLoading}
            />
          </Box>
        </Box>
        <FormManageFooterActions
          isEditMode={isEditMode}
          isBusy={isBusy}
          handleBackToFeeds={handleBackToFeeds}
        />
      </Stack>
    </>
  );
});

export default FormManage;
